import { useEffect, useRef, useState } from "react";
import { Message } from "./types";
import { useSupabaseUser } from "@/hooks/use-supabase-user";
import { Button } from "@/components/ui/button";
import { Trash2, Check, Clock, Edit2 } from "lucide-react";
import { format } from "date-fns";
import { useTheme } from "@/contexts/ThemeContext";
import { supabase } from "@/integrations/supabase/client";
import { ImagePreview } from "./ImagePreview";
import { MessageContextMenu } from "./MessageContextMenu";
import { DeleteConfirmDialog } from "./DeleteConfirmDialog";

interface MessageListProps {
  messages: Message[];
  isAdmin: boolean;
  onDelete: (messageId: string) => void;
  onEdit: (messageId: string, newContent: string) => void;
}

export const MessageList = ({ messages, isAdmin, onDelete, onEdit }: MessageListProps) => {
  const { user } = useSupabaseUser();
  const { theme } = useTheme();
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const scrollAnchorRef = useRef<HTMLDivElement>(null);
  const [imageErrorCount, setImageErrorCount] = useState<Record<string, number>>({});
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    messageId: string;
    messageContent: string;
    isOwnMessage: boolean;
  } | null>(null);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    messageId: string;
    messageContent: string;
  }>({ open: false, messageId: "", messageContent: "" });
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editContent, setEditContent] = useState("");

  useEffect(() => {
    if (messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  }, [messages.length]);

  const handleContextMenu = (e: React.MouseEvent, message: Message) => {
    e.preventDefault();
    const isOwnMessage = message.author?.id === user?.id;
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      messageId: message.id,
      messageContent: message.content || "",
      isOwnMessage,
    });
  };

  const handleStartEdit = (messageId: string, content: string) => {
    setEditingMessageId(messageId);
    setEditContent(content);
  };

  const handleCancelEdit = () => {
    setEditingMessageId(null);
    setEditContent("");
  };

  const handleSaveEdit = async () => {
    if (!editingMessageId || !editContent.trim()) return;
    
    await onEdit(editingMessageId, editContent);
    setEditingMessageId(null);
    setEditContent("");
  };

  const handleDeleteClick = (messageId: string, content: string) => {
    setDeleteDialog({
      open: true,
      messageId,
      messageContent: content || "Image message",
    });
  };

  const handleConfirmDelete = async () => {
    if (deleteDialog.messageId) {
      await onDelete(deleteDialog.messageId);
    }
    setDeleteDialog({ open: false, messageId: "", messageContent: "" });
  };

  useEffect(() => {
    const updateUnknownUserProfiles = async () => {
      if (!messages.length) return;
      
      const problematicUserIds = messages
        .filter(m => 
          !m.author?.full_name || 
          m.author.full_name === 'Unknown User' || 
          m.author.full_name.startsWith('User-')
        )
        .map(m => m.author?.id)
        .filter(id => id);
      
      if (problematicUserIds.length === 0) return;
      
      console.log("Found problematic user IDs that need profile updates:", problematicUserIds);
      
      for (const userId of problematicUserIds) {
        try {
          const { data: isAdminUser } = await supabase.rpc('has_role', {
            user_id: userId,
            _role: 'admin'
          });
          
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('email, first_name, last_name, created_at')
            .eq('id', userId)
            .single();
            
          if (profileError && profileError.code !== 'PGRST116') {
            console.error("Error fetching profile:", profileError);
            continue;
          }
          
          let authUserData = null;
          try {
            const { data: authData } = await supabase.auth.getUser(userId);
            if (authData?.user) {
              authUserData = authData.user;
            }
          } catch (authError) {
            console.log("Auth data fetch failed, continuing with profile data only:", authError);
          }
          
          let fullName = '';
          
          if (authUserData?.user_metadata?.full_name) {
            fullName = authUserData.user_metadata.full_name;
          } else if (authUserData?.user_metadata?.name) {
            fullName = authUserData.user_metadata.name;
          } else if (authUserData?.user_metadata?.first_name && authUserData?.user_metadata?.last_name) {
            fullName = `${authUserData.user_metadata.first_name} ${authUserData.user_metadata.last_name}`;
          } else if (profile?.first_name && profile?.last_name) {
            fullName = `${profile.first_name} ${profile.last_name}`;
          } else if (profile?.email) {
            const emailPrefix = profile.email.split('@')[0];
            fullName = emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1);
          } else if (authUserData?.email) {
            const emailPrefix = authUserData.email.split('@')[0];
            fullName = emailPrefix.charAt(0).toUpperCase() + emailPrefix.slice(1);
          }
          
          if (!fullName) {
            fullName = `Team Member`;
          }
          
          if (isAdminUser) {
            if (fullName && !fullName.includes('Admin')) {
              fullName = `${fullName} (Admin)`;
            } else if (!fullName) {
              fullName = 'Admin User';
            }
          }
          
          if (fullName && !fullName.startsWith('User-') && fullName !== 'Unknown User') {
            console.log(`Updating profile for ${userId} with name: ${fullName}`);
            
            if (!profile) {
              const { error: insertError } = await supabase
                .from('profiles')
                .insert({
                  id: userId,
                  full_name: fullName,
                  email: authUserData?.email,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString()
                });
                
              if (insertError) {
                console.error("Error creating profile:", insertError);
              } else {
                console.log(`Successfully created profile for ${userId}`);
              }
            } else {
              const { error: updateError } = await supabase
                .from('profiles')
                .update({
                  full_name: fullName,
                  updated_at: new Date().toISOString()
                })
                .eq('id', userId);
                
              if (updateError) {
                console.error("Error updating profile:", updateError);
              } else {
                console.log(`Successfully updated profile for ${userId}`);
              }
            }
          }
        } catch (error) {
          console.error(`Error processing user ${userId}:`, error);
        }
      }
    };
    
    updateUnknownUserProfiles();
  }, [messages]);

  useEffect(() => {
    if (messages.length > 0) {
      console.log("Messages in MessageList:", messages.map(m => ({
        id: m.id,
        author: m.author ? {
          id: m.author.id,
          name: m.author.full_name || 'No name provided'
        } : 'No author',
        content: m.content?.substring(0, 20) + '...'
      })));
    }
    console.log("Current user ID in MessageList:", user?.id);
    console.log("Admin status in MessageList:", isAdmin);
  }, [messages, user, isAdmin]);

  if (!user) {
    console.log("No user found in MessageList");
    return <div className="flex-1 flex items-center justify-center p-6">
      <div className="animate-pulse bg-primary-50 p-4 rounded-lg text-primary-600">
        Loading messages...
      </div>
    </div>;
  }

  const handleImageError = (imageUrl: string, event: React.SyntheticEvent<HTMLImageElement>) => {
    const currentErrorCount = imageErrorCount[imageUrl] || 0;
    const newErrorCount = currentErrorCount + 1;
    setImageErrorCount(prev => ({ ...prev, [imageUrl]: newErrorCount }));
    
    console.log(`Error loading image: ${imageUrl}, attempt: ${newErrorCount}`);
    
    if (newErrorCount >= 3) {
      (event.target as HTMLImageElement).src = "https://images.unsplash.com/photo-1578496479531-32dd67a7ccf5?w=500&dpr=1";
      return;
    }
    
    const imgSrc = event.currentTarget;
    
    const urlParts = imageUrl.split('/');
    const fileName = urlParts[urlParts.length - 1];
    
    const fallbackPaths = [
      `/team-chat-images/${fileName}`,
      `/storage/v1/object/public/team-chat-images/${fileName}`,
      `https://igcqqrcdtqpecopvuuva.supabase.co/storage/v1/object/public/team-chat-images/${fileName}`
    ];
    
    console.log("Attempting fallback paths...", fallbackPaths);
    imgSrc.src = fallbackPaths[newErrorCount % fallbackPaths.length];
  };

  const resolveAuthorName = (message: Message): string => {
    if (message.author?.id && message.author.id.startsWith('4de1cc6d')) {
      return "Operations";
    }
    
    if (message.author?.id === user.id) {
      return isAdmin ? "Admin" : "You";
    }
    
    if (message.author?.full_name && 
        message.author.full_name !== 'Unknown User' && 
        !message.author.full_name.startsWith('User-')) {
      return message.author.full_name;
    }
    
    if (message.author?.id) {
      const idParts = message.author.id.split('-')[0];
      if (idParts) {
        return `Operations`;
      }
    }
    
    return message.author?.full_name || 'Operations';
  };

  const isSupabaseStorageUrl = (url: string): boolean => {
    return url && (
      url.includes('supabase.co/storage') || 
      url.includes('/storage/v1/object') ||
      url.includes('team-chat-images') ||
      url.includes('chat-images')
    );
  };

  const renderMessageImage = (imageUrl: string) => {
    if (!imageUrl) return null;
    
    return (
      <div 
        className="max-w-xs sm:max-w-sm rounded-xl mb-2 relative cursor-pointer overflow-hidden group shadow-lg"
        onClick={() => setPreviewImage(imageUrl)}
      >
        <picture>
          {isSupabaseStorageUrl(imageUrl) && (
            <>
              <source srcSet={imageUrl} type="image/webp" />
              <source srcSet={imageUrl} type="image/jpeg" />
              <source srcSet={imageUrl} type="image/png" />
            </>
          )}
          <img
            src={imageUrl}
            alt="Message attachment"
            className="w-full h-auto rounded-xl object-cover max-h-[200px] group-hover:scale-105 transition-all duration-300"
            onError={(e) => handleImageError(imageUrl, e)}
            loading="lazy"
          />
        </picture>
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300 rounded-xl" />
      </div>
    );
  };

  return (
    <>
      <div
        ref={messagesContainerRef}
        className="flex-1 overflow-y-auto p-6 space-y-6 h-full min-h-0 scrollbar-thin scrollbar-thumb-border/30 scrollbar-track-transparent"
        style={{
          maxHeight: "100%",
        }}
        tabIndex={0}
      >
        {/* Date Header */}
        <div className="text-center sticky top-0 z-10">
          <div className="inline-block bg-background/80 backdrop-blur-sm px-4 py-2 rounded-full text-xs text-muted-foreground border border-border/50 shadow-sm">
            {format(new Date(), "EEEE, MMMM d, yyyy")}
          </div>
        </div>
        
        {/* Messages */}
        {messages.map((message, index) => {
          const isCurrentUser = message.author?.id === user.id;
          const authorName = resolveAuthorName(message);
          const previousMessage = index > 0 ? messages[index - 1] : null;
          const showAvatar = !previousMessage || previousMessage.author?.id !== message.author?.id;
          
          return (
            <div
              key={message.id}
              className={`flex items-end gap-3 animate-fade-in ${
                isCurrentUser ? "flex-row-reverse" : "flex-row"
              }`}
            >
              {/* Avatar */}
              <div className={`flex-shrink-0 ${showAvatar ? 'opacity-100' : 'opacity-0'}`}>
                <div className={`h-8 w-8 rounded-full bg-gradient-to-br from-primary to-secondary flex items-center justify-center text-white text-sm font-medium shadow-lg ${isCurrentUser ? 'from-primary/80 to-primary' : 'from-secondary/80 to-secondary'}`}>
                  {authorName.charAt(0).toUpperCase()}
                </div>
              </div>
              
            {/* Message Content */}
              <div className={`max-w-[70%] space-y-1 ${isCurrentUser ? 'items-end' : 'items-start'} flex flex-col group`}>
                {/* Author name (only show for first message in a group) */}
                {showAvatar && !isCurrentUser && (
                  <div className="text-xs text-muted-foreground font-medium ml-1">
                    {authorName}
                  </div>
                )}
                
                {/* Message bubble */}
                <div
                  onContextMenu={(e) => handleContextMenu(e, message)}
                  className={`relative p-4 shadow-md transition-all duration-200 hover:shadow-lg cursor-pointer ${
                    isCurrentUser
                      ? "bg-gradient-to-br from-primary to-primary/90 text-primary-foreground rounded-2xl rounded-br-md"
                      : "bg-background/80 backdrop-blur-sm text-foreground rounded-2xl rounded-bl-md border border-border/50"
                  }`}
                >
                  {/* Message content or edit mode */}
                  {editingMessageId === message.id ? (
                    <div className="space-y-2">
                      <textarea
                        value={editContent}
                        onChange={(e) => setEditContent(e.target.value)}
                        className="w-full min-h-[60px] p-2 rounded-lg bg-background/50 border border-border text-foreground resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            handleSaveEdit();
                          } else if (e.key === "Escape") {
                            handleCancelEdit();
                          }
                        }}
                      />
                      <div className="flex gap-2 justify-end">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={handleCancelEdit}
                          className="h-7"
                        >
                          Cancel
                        </Button>
                        <Button
                          size="sm"
                          onClick={handleSaveEdit}
                          className="h-7"
                          disabled={!editContent.trim()}
                        >
                          Save
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      {message.image_url && renderMessageImage(message.image_url)}
                      {message.content && (
                        <p className="break-words leading-relaxed">
                          {message.content}
                        </p>
                      )}
                    </>
                  )}
                </div>
                
                {/* Message metadata */}
                <div
                  className={`flex items-center gap-2 text-xs text-muted-foreground/70 px-1 ${
                    isCurrentUser ? "justify-end" : "justify-start"
                  }`}
                >
                  <div className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    <span>{format(new Date(message.created_at), "HH:mm")}</span>
                  </div>
                  
                  {message.updated_at && message.updated_at !== message.created_at && (
                    <span className="text-xs italic">(edited)</span>
                  )}
                  
                  {isCurrentUser && (
                    <Check className="h-3 w-3 text-primary/70" />
                  )}
                </div>
              </div>
            </div>
          );
        })}
        <div ref={scrollAnchorRef} />
      </div>

      {previewImage && (
        <ImagePreview 
          isOpen={!!previewImage} 
          onClose={() => setPreviewImage(null)}
          imageUrl={previewImage}
        />
      )}

      {contextMenu && (
        <MessageContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          onEdit={() => handleStartEdit(contextMenu.messageId, contextMenu.messageContent)}
          onDelete={() => handleDeleteClick(contextMenu.messageId, contextMenu.messageContent)}
          onClose={() => setContextMenu(null)}
          isOwnMessage={contextMenu.isOwnMessage}
          isAdmin={isAdmin}
          messageContent={contextMenu.messageContent}
        />
      )}

      <DeleteConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => !open && setDeleteDialog({ open: false, messageId: "", messageContent: "" })}
        onConfirm={handleConfirmDelete}
        messagePreview={deleteDialog.messageContent}
      />
    </>
  );
};
